import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { ZIMCallUserState } from 'zego-zim-web';

import { ZegoMediaControlService } from '../../../services/zego-media-control.service';
import { ZimService } from '../../../services/zim.service';
import { CALL_INCOMING_STATUS, CALL_STATE } from '../constants';
import {
  CallEndReason,
  CallInfo,
  CallInvitation,
  CallParticipant,
  CallState,
  CallType,
} from '../types/call';

@Injectable({
  providedIn: 'root',
})
export class CallSessionStoreService implements OnDestroy {
  private zimService = inject(ZimService);
  private zegoMediaControlService = inject(ZegoMediaControlService);

  callInfo = signal<CallInfo | null>(null);
  callState = signal<CallState>(CALL_STATE.IDLE);
  _participants = signal<CallParticipant[]>([]);

  pendingInvitation = signal<CallInvitation | null>(null);
  callStartTime = signal<number>(0);
  beforeCallType = signal<CallType>('audio');
  callDurationText = signal<string>('00:00');

  readonly remoteSoundLevels = signal<Partial<Record<string, number>>>({});

  private durationIntervalId: ReturnType<typeof setInterval> | null = null;

  callType = computed<CallType | undefined>(() => this.callInfo()?.type);
  callEndReason = computed<CallEndReason | undefined>(() => this.callInfo()?.endReason);
  hasCurrentCall = computed<boolean>(() => !!this.callInfo());

  // Allow compatibility with components using currentCall
  currentCall = this.callInfo;

  participants = computed(() => {
    if (this.callState() !== CALL_STATE.IDLE) {
      return this._participants();
    }
    const pending = this.pendingInvitation();
    return pending?.invitees ?? [];
  });

  readonly pendingParticipants = computed(() => this.pendingInvitation()?.invitees ?? []);

  isMeClickAccept = signal<boolean>(false);
  isMeClickCall = signal<boolean>(false);
  isLoginRoom = signal<boolean>(false);

  readonly isInCall = computed(() => this.callState() !== CALL_STATE.IDLE);
  readonly activeParticipants = computed(() =>
    this.participants().filter((p) => p.state === ZIMCallUserState.Accepted),
  );
  readonly hasPendingCall = computed(
    () => this.pendingInvitation() && this.callState() === CALL_STATE.IDLE,
  );
  readonly hasCalleeIncomingCall = computed(
    () =>
      this.pendingInvitation() &&
      this.pendingInvitation()?.status !== CALL_INCOMING_STATUS.WAITING &&
      this.pendingInvitation()?.inviter !== this.zimService.userId,
  );

  readonly showCallStatusBar = computed(() => {
    const pending = this.pendingInvitation();
    if (pending) return true;

    const state = this.callState();
    if (state === CALL_STATE.IDLE || state === CALL_STATE.ENDED) return false;

    const call = this.callInfo();
    if (!call) return false;

    const isCaller = call.inviter === this.zimService.userId;
    if (isCaller) return true;

    return state === CALL_STATE.CONNECTED || state === CALL_STATE.CONNECTING;
  });

  readonly isInviter = computed(() => {
    const call = this.callInfo();
    return call && call.inviter === this.zimService.userId;
  });

  setPendingInvitation(pendingInvitation: CallInvitation | null): void {
    this.pendingInvitation.set(pendingInvitation);
  }

  setCurrentCall(callInfo: CallInfo | null): void {
    this.callInfo.set(callInfo);
  }

  updateCurrentCall(updater: (info: CallInfo | null) => CallInfo | null): void {
    this.callInfo.update(updater);
  }

  setParticipants(participants: CallParticipant[]): void {
    if (this.callInfo()) {
      this._participants.set(participants);
      return;
    }
    const pending = this.pendingInvitation();
    if (pending) {
      this.pendingInvitation.set({ ...pending, invitees: participants });
    }
  }

  updateParticipants(updater: (participants: CallParticipant[]) => CallParticipant[]): void {
    this.setParticipants(updater(this.participants()));
  }

  setCurrentCallState(callState: CallState): void {
    if (callState === CALL_STATE.IDLE) {
      this.callInfo.set(null);
      this.callState.set(CALL_STATE.IDLE);
      this._participants.set([]);
      return;
    }

    if (this.callInfo()) {
      this.callState.set(callState);
      return;
    }

    const pending = this.pendingInvitation();
    if (pending) {
      this.callInfo.set({
        callId: pending.callId,
        type: pending.type,
        startTime: pending.createTime || new Date(),
        inviter: pending.inviter,
        extendedData: pending.extendedData,
      });
      this.callState.set(callState);
      this._participants.set(pending.invitees);
    }
  }

  setCallStartTime(callStartTime: number): void {
    this.callStartTime.set(callStartTime);
    this.startDurationTimer(callStartTime);
  }

  private startDurationTimer(startTime: number): void {
    this.stopDurationTimer();

    const updateTimer = () => {
      const diff = Date.now() - startTime;
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);

      this.callDurationText.set(
        (h > 0 ? `${h.toString().padStart(2, '0')}:` : '') +
          `${m.toString().padStart(2, '0')}:` +
          `${s.toString().padStart(2, '0')}`,
      );
    };

    updateTimer();
    this.durationIntervalId = setInterval(updateTimer, 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationIntervalId) {
      clearInterval(this.durationIntervalId);
      this.durationIntervalId = null;
    }
  }

  ngOnDestroy(): void {
    this.stopDurationTimer();
  }

  getNameParticipantByUserID(userID: string) {
    return this.participants().find((p) => p.userId === userID)?.userName ?? userID;
  }

  reset(preservePending = false): void {
    this.stopDurationTimer();
    const pending = preservePending ? this.pendingInvitation() : null;
    this.callInfo.set(null);
    this.callState.set(CALL_STATE.IDLE);
    this._participants.set([]);
    if (!preservePending) {
      this.pendingInvitation.set(null);
    } else if (pending) {
      // Giữ cuộc gọi chờ (WAITING) khi cuộc gọi hiện tại end/timeout/network.
      this.pendingInvitation.set(pending);
    }
    this.beforeCallType.set('audio');
    this.callStartTime.set(0);
    this.callDurationText.set('00:00');
    this.remoteSoundLevels.set({});
    this.zegoMediaControlService.resetState();
  }

  /** Chỉ xóa cuộc gọi hiện tại, giữ nguyên pendingInvitation (dùng khi còn cuộc gọi chờ). */
  resetCurrentCallOnly(): void {
    this.reset(true);
  }
}
